import {useEffect, useRef, useState} from "react";

import {GuideStateApi, useGuideState} from "@khan/guide-state";

import {isActiveGuideConversation} from "../utils/is-active-guide-conversation.ts";

import {useGuideStateChangeEffect} from "./use-guide-state-change-effect.ts";

/**
 * A hook that invokes the given callback at most once during a conversation
 * when the thread becomes active. A thread is considered "active" when there
 * is at least one user (outgoing) message in the conversation.
 *
 * Note: The callback can be invoked more than once for a conversation if it
 * is restored.
 */
export const useOnGuideThreadIsActiveEffect = (onThreadActive: () => void) => {
    const [isActive, setIsActive] = useState(
        isActiveGuideConversation(GuideStateApi.latest().conversation),
    );
    const threadId = useGuideState("threadId");

    useGuideStateChangeEffect("conversation", ({conversation}) => {
        // The difference using useGuideStateChangeEffect and subscribing to
        // the "conversation" React state via useGuideState("conversation") is
        // that we won't have to worry about unnecessary re-renders caused by
        // the updates to the "conversation" React state.
        //
        // Since the useGuideStateChangeEffect callback is triggered before the
        // state change has "propagated into React state" (it gets synced in
        // a subsequent render), we defer calling `onThreadActive` until the
        // next render by setting a React state variable here and handling the
        // callback in a useEffect hook. This ensures that the callback is only
        // invoked when the React state is aware that the conversation is
        // active to achieve the desired behavior.
        setIsActive(isActiveGuideConversation(conversation));
    });

    // Effect for invoking the callback when the thread becomes active, we
    // ensure that the callback is only invoked once per thread.
    const lastInvokedThreadId = useRef<string | null>(null);
    useEffect(() => {
        if (!threadId) {
            return;
        }

        if (lastInvokedThreadId.current === threadId) {
            return;
        }

        if (isActive) {
            onThreadActive();
            lastInvokedThreadId.current = threadId;
        }
    }, [threadId, onThreadActive, isActive]);
};
